import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import EditParameterModal from './EditParameterModal';
import PDF from './PDF';
import Sidebar from './Sidebar';
import {
  LineChart, Line, BarChart, Bar, ScatterChart, Scatter, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import TableWithPagination from './TableWithPagination';

const KatalogAnalisis = ({ activeTabProp }) => {
  const [activeTab, setActiveTab] = useState(activeTabProp || 'analisis');
  const navigate = useNavigate();
  const { isLoading, error, fetchData, executeQuery } = useData();
  const { user, isAdmin, isAnalis, isEndUser } = useAuth();
  const pdfRef = useRef(null);
  
  const [visualisasiData, setVisualisasiData] = useState([]);
  const [parameterData, setParameterData] = useState([]);
  const [analisisData, setAnalisisData] = useState([]);
  const [selectedAnalisis, setSelectedAnalisis] = useState(null);
  const [previewVisualisasi, setPreviewVisualisasi] = useState([]);
  const [chartDataMap, setChartDataMap] = useState({});
  
  const [successMessage, setSuccessMessage] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  
  const [availableDatabases, setAvailableDatabases] = useState([]);
  const [selectedDatabase, setSelectedDatabase] = useState('');

  const [showEditParameterModal, setShowEditParameterModal] = useState(false);
  const [editingVisualisasi, setEditingVisualisasi] = useState(null);

  const [isEditingAnalisis, setIsEditingAnalisis] = useState(false);
  const [editedJudul, setEditedJudul] = useState('');
  const [editedMasalah, setEditedMasalah] = useState('');
  const [editedInterpretasi, setEditedInterpretasi] = useState('');
  const [tampilkanTabelMap, setTampilkanTabelMap] = useState({});

  useEffect(() => {
    if (activeTabProp) setActiveTab(activeTabProp);
  }, [activeTabProp]);

  const fetchAvailableDatabases = async () => {
    try {
      const response = await fetch(`/api/databases`);
      if (response.ok) {
        const databases = await response.json();
        setAvailableDatabases(databases);
        if (databases.length > 0) {
          setSelectedDatabase(databases[0].id_database);
        }
      }
    } catch (error) {
      console.error('Error fetching databases:', error);
    }
  };

  useEffect(() => {
    fetchAvailableDatabases();
  }, []);
  
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const visualisasiResponse = await fetch(`/api/database/${selectedDatabase}/visualisasi`);
        if (visualisasiResponse.ok) {
          const visualisasi = await visualisasiResponse.json();
          const sortedVisualisasi = [...visualisasi].sort((a, b) =>
            new Date(b.created_at) - new Date(a.created_at)
          );
          setVisualisasiData(sortedVisualisasi);
        }

        const analisisResponse = await fetch(`/api/database/${selectedDatabase}/analisis-with-visualisasi`);
        if (analisisResponse.ok) {
          const analisis = await analisisResponse.json();
          setAnalisisData(analisis);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };
    if (selectedDatabase) fetchAllData();
  }, [selectedDatabase]);
  
  const handleAnalisisSelect = async (analisis) => {
    if (selectedAnalisis && selectedAnalisis.id_analisis === analisis.id_analisis) {
      setSelectedAnalisis(null);
      setPreviewVisualisasi([]);
      setChartDataMap({});
      setTampilkanTabelMap({});
      setIsEditingAnalisis(false);
      return;
    }
    setSelectedAnalisis(analisis);
    setEditedJudul(analisis.judul || '');
    setEditedMasalah(analisis.rumusan_masalah || '');
    setEditedInterpretasi(analisis.interpretasi_hasil || '');
    setIsEditingAnalisis(false);
    
    try {
      const idsResponse = await fetch(`/api/analisis/${analisis.id_analisis}/visualisasi`);
      if (!idsResponse.ok) throw new Error('Gagal mengambil data visualisasi untuk analisis');
      
      const visualisasiIds = await idsResponse.json();
      if (visualisasiIds.length === 0) {
        setPreviewVisualisasi([]);
        setChartDataMap({});
        return;
      }
      
      const visualisasiPromises = visualisasiIds.map(id =>
        fetch(`/api/visualizations/${id}`).then(res => res.json())
      );
      const relatedVisualizations = await Promise.all(visualisasiPromises);
      
      if (relatedVisualizations.length > 0) {
        const newChartDataMap = {};
        for (const vis of relatedVisualizations) {
          newChartDataMap[vis.id_visualisasi] = await executeVisualizationQuery(vis);
        }
        setChartDataMap(newChartDataMap);
        setPreviewVisualisasi(relatedVisualizations);
        const initTampilkan = {};
        relatedVisualizations.forEach(v => { initTampilkan[v.id_visualisasi] = !!v.tampilkan_tabel; });
        setTampilkanTabelMap(initTampilkan);
      } else {
        setPreviewVisualisasi([]);
        setChartDataMap({});
      }
    } catch (err) {
      console.error('Error loading visualizations for analysis:', err);
    }
  };

  const parseParameterQuery = (parameterQuery) => {
    if (!parameterQuery) return {};
    try {
      const params = typeof parameterQuery === 'string'
        ? JSON.parse(parameterQuery)
        : parameterQuery;
      return params && typeof params === 'object' ? params : {};
    } catch (error) {
      console.error('Error parsing visualization parameters:', error);
      return {};
    }
  };

  const getVisualizationDatabaseId = (visualisasi) => (
    visualisasi?.penyimpanan_database || visualisasi?.database || selectedDatabase || null
  );

  const executeVisualizationQuery = async (visualisasi) => {
    if (!visualisasi?.query_sql) return [];
    const params = parseParameterQuery(visualisasi.parameter_query);
    const databaseId = getVisualizationDatabaseId(visualisasi);
    const result = await executeQuery(visualisasi.query_sql, params, databaseId);
    return Array.isArray(result) ? result : [];
  };

  const handleEditParameter = (visualisasi) => {
    setEditingVisualisasi(visualisasi);
    setShowEditParameterModal(true);
  };

  const handleSaveParameterEdit = (newChartData, params) => {
    setChartDataMap(prev => ({
      ...prev,
      [editingVisualisasi.id_visualisasi]: newChartData
    }));
    setSaveSuccess(true);
    setSuccessMessage('Parameter berhasil diperbarui dan visualisasi di-refresh!');
    setTimeout(() => { setSaveSuccess(false); setSuccessMessage(''); }, 3000);
  };

  const handleStartEditAnalisis = () => setIsEditingAnalisis(true);

  const handleCancelEditAnalisis = () => {
    setIsEditingAnalisis(false);
    setEditedJudul(selectedAnalisis.judul || '');
    setEditedMasalah(selectedAnalisis.rumusan_masalah || '');
    setEditedInterpretasi(selectedAnalisis.interpretasi_hasil || '');
  };

  const handleSaveEditAnalisis = async () => {
    try {
      const response = await fetch(`/api/analisis/${selectedAnalisis.id_analisis}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ judul: editedJudul, masalah: editedMasalah, interpretasi_hasil: editedInterpretasi }),
      });
      if (!response.ok) throw new Error('Gagal menyimpan perubahan analisis');
      const updatedAnalisisData = analisisData.map(item =>
        item.id_analisis === selectedAnalisis.id_analisis
          ? { ...item, judul: editedJudul, rumusan_masalah: editedMasalah, interpretasi_hasil: editedInterpretasi }
          : item
      );
      setAnalisisData(updatedAnalisisData);
      setSelectedAnalisis({ ...selectedAnalisis, judul: editedJudul, rumusan_masalah: editedMasalah, interpretasi_hasil: editedInterpretasi });
      setIsEditingAnalisis(false);
      setSaveSuccess(true);
      setSuccessMessage('Analisis berhasil diperbarui!');
      setTimeout(() => { setSaveSuccess(false); setSuccessMessage(''); }, 3000);
    } catch (err) {
      console.error('Error saving analisis:', err);
      setSaveError(true);
      setTimeout(() => setSaveError(false), 3000);
    }
  };

  const handleSaveTampilkanTabelVis = async (id, value) => {
    try {
      const response = await fetch(`/api/visualizations/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tampilkan_tabel: value ? 1 : 0 })
      });
      if (!response.ok) throw new Error('Gagal menyimpan');
      const newVal = value ? 1 : 0;
      setPreviewVisualisasi(prev => prev.map(v =>
        v.id_visualisasi === id ? { ...v, tampilkan_tabel: newVal } : v
      ));
      setSaveSuccess(true);
      setSuccessMessage('Pengaturan tabel berhasil disimpan!');
      setTimeout(() => { setSaveSuccess(false); setSuccessMessage(''); }, 3000);
    } catch (e) {
      setSaveError(true);
      setTimeout(() => setSaveError(false), 3000);
    }
  };

  const parseChartData = (data, xKey, yKey, chartType) => {
    if (!data) return [];
    return data.map(item => {
      const parsedItem = { ...item };
      if (yKey && parsedItem[yKey] !== undefined && parsedItem[yKey] !== null) {
        const numValue = parseFloat(parsedItem[yKey]);
        if (!isNaN(numValue)) parsedItem[yKey] = numValue;
      }
      if (chartType === 'scatter' && xKey && parsedItem[xKey] !== undefined && parsedItem[xKey] !== null) {
        const numValue = parseFloat(parsedItem[xKey]);
        if (!isNaN(numValue)) parsedItem[xKey] = numValue;
      }
      return parsedItem;
    });
  };
  
  const renderChart = (visualisasi, chartData) => {
    if (!chartData || chartData.length === 0) {
      return <div className="no-chart-data">Tidak ada data untuk visualisasi ini</div>;
    }
    const dataKey = visualisasi.parameter_x;
    const valueKey = visualisasi.parameter_y;
    if (!dataKey || !valueKey) {
      return <div className="no-chart-data">Parameter visualisasi tidak lengkap</div>;
    }
    if (visualisasi.jenis_grafik === 'table') {
      return <TableWithPagination data={chartData} />;
    }
    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#8DD1E1'];
    const parsedChartData = parseChartData(chartData, dataKey, valueKey, visualisasi.jenis_grafik);
    return (
      <ResponsiveContainer width="100%" height={400}>
        {visualisasi.jenis_grafik === 'bar' ? (
          <BarChart data={parsedChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={dataKey} /><YAxis /><Tooltip />
            <Legend payload={[{ value: `Sumbu X: ${dataKey}`, type: 'line', color: '#666' }, { value: `Sumbu Y: ${valueKey}`, type: 'rect', color: '#8884d8' }]} />
            <Bar dataKey={valueKey} fill="#8884d8" name={valueKey} />
          </BarChart>
        ) : visualisasi.jenis_grafik === 'line' ? (
          <LineChart data={parsedChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={dataKey} /><YAxis /><Tooltip />
            <Legend payload={[{ value: `Sumbu X: ${dataKey}`, type: 'line', color: '#666' }, { value: `Sumbu Y: ${valueKey}`, type: 'line', color: '#8884d8' }]} />
            <Line type="monotone" dataKey={valueKey} stroke="#8884d8" name={valueKey} />
          </LineChart>
        ) : visualisasi.jenis_grafik === 'pie' ? (
          <PieChart>
            <Pie data={parsedChartData} cx="50%" cy="50%" labelLine={true} outerRadius={150} fill="#8884d8" dataKey={valueKey} nameKey={dataKey} label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend payload={[{ value: `Category: ${dataKey}`, type: 'rect', color: '#666' }, { value: `Values: ${valueKey}`, type: 'rect', color: '#8884d8' }]} />
          </PieChart>
        ) : (
          <ScatterChart>
            <CartesianGrid />
            <XAxis type="number" dataKey={dataKey} name={dataKey} />
            <YAxis type="number" dataKey={valueKey} name={valueKey} />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} />
            <Legend payload={[{ value: `Sumbu X: ${dataKey}`, type: 'rect', color: '#666' }, { value: `Sumbu Y: ${valueKey}`, type: 'rect', color: '#8884d8' }]} />
            <Scatter name={`${dataKey} vs ${valueKey}`} data={parsedChartData} fill="#8884d8" />
          </ScatterChart>
        )}
      </ResponsiveContainer>
    );
  };
  
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('id-ID', options);
  };

  const handleDeleteAnalisis = async (id) => {
    if (!isAdmin() && !isAnalis()) {
      alert('Anda tidak memiliki izin untuk menghapus analisis!');
      return;
    }
    if (window.confirm('Apakah Anda yakin ingin menghapus analisis ini?')) {
      try {
        const response = await fetch(`/api/analisis/${id}`, { method: 'DELETE' });
        if (!response.ok) throw new Error('Gagal menghapus analisis');
        if (selectedAnalisis && selectedAnalisis.id_analisis === id) {
          setSelectedAnalisis(null);
          setPreviewVisualisasi([]);
          setChartDataMap({});
        }
        const getResponse = await fetch(`/api/database/${selectedDatabase}/analisis-with-visualisasi`);
        if (getResponse.ok) {
          const analisis = await getResponse.json();
          setAnalisisData(analisis);
        }
        setSaveSuccess(true);
        setSuccessMessage("Analisis berhasil dihapus!");
        setTimeout(() => { setSaveSuccess(false); setSuccessMessage(""); }, 3000);
      } catch (err) {
        console.error('Error deleting analisis:', err);
        setSaveError(true);
        setTimeout(() => setSaveError(false), 3000);
      }
    }
  };

  const downloadPDF = async () => {
    const success = await PDF.downloadAnalisisPDF(selectedAnalisis, previewVisualisasi);
    if (success) {
      setDownloadSuccess(true);
      setTimeout(() => setDownloadSuccess(false), 3000);
    }
  };

  const handleCreateAnalisis = () => {
    if (!isAdmin() && !isAnalis()) {
      alert('Anda tidak memiliki izin untuk membuat analisis!');
      return;
    }
    navigate('/analisis/buat');
  };

  return (
    <Sidebar activeTabProp={activeTab}>
      {isLoading ? (
        <div className="loading"><div className="loading-spinner"></div><p>Memuat data...</p></div>
      ) : error ? (
        <div className="error"><i className="fas fa-exclamation-triangle"></i> Error: {error}</div>
      ) : (
        <div className="dashboard-content">
          <div className="data-explorer">
            <h1>Katalog Analisis</h1>

            <div className="database-viewing-section" style={{ marginBottom: '20px' }}>
              <h2>Pilih Database untuk Dilihat</h2>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Database:</label>
                <select
                  value={selectedDatabase}
                  onChange={(e) => setSelectedDatabase(parseInt(e.target.value))}
                  style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd', backgroundColor: 'white', minWidth: '200px' }}
                >
                  {availableDatabases.map((database) => (
                    <option key={database.id_database} value={database.id_database}>
                      {database.name} ({database.host}:{database.port || 'default'})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="catalog-section">
              <h2>Katalog Analisis</h2>
              {saveSuccess && <div className="save-success-message">{successMessage}</div>}
              
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Judul</th>
                      <th>Visualisasi</th>
                      <th>Tanggal Dibuat</th>
                      <th>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analisisData.length > 0 ? (
                      analisisData.map((item) => (
                        <tr key={item.id_analisis} className={selectedAnalisis && selectedAnalisis.id_analisis === item.id_analisis ? 'selected-row' : ''}>
                          <td>{item.judul}</td>
                          <td>
                            {item.visualisasi_judul || '-'}
                            {item.jumlah_visualisasi > 1 && <span className="visualisasi-count"> ({item.jumlah_visualisasi})</span>}
                          </td>
                          <td>{formatDate(item.created_at)}</td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className={selectedAnalisis && selectedAnalisis.id_analisis === item.id_analisis ? 'cancel-button' : 'view-button'}
                                onClick={() => handleAnalisisSelect(item)}
                              >
                                {selectedAnalisis && selectedAnalisis.id_analisis === item.id_analisis ? 'Tutup' : 'Lihat'}
                              </button>
                              {(isAdmin() || isAnalis()) && (
                                <button className="delete-button" onClick={(e) => { e.stopPropagation(); handleDeleteAnalisis(item.id_analisis); }}>
                                  Hapus
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="4" className="no-data">Belum ada analisis tersimpan</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {selectedAnalisis && (
                <div className="analisis-preview" id="analisis-preview">
                  <h2>Preview Analisis</h2>
                  <div className="analisis-content">
                    {isEditingAnalisis && (isEndUser() || isAnalis()) ? (
                      <div className="form-group">
                        <label>Judul Analisis:</label>
                        <input type="text" className="form-input" value={editedJudul} onChange={(e) => setEditedJudul(e.target.value)} />
                      </div>
                    ) : (
                      <h3>{selectedAnalisis.judul}</h3>
                    )}
                    
                    <div className="analisis-section">
                      <h4>Rumusan Masalah:</h4>
                      {isEditingAnalisis && (isEndUser() || isAnalis()) ? (
                        <textarea className="form-textarea" value={editedMasalah} onChange={(e) => setEditedMasalah(e.target.value)} rows={4} />
                      ) : (
                        <p>{selectedAnalisis.rumusan_masalah}</p>
                      )}
                    </div>
                    
                    <div className="analisis-section">
                      <h4>Visualisasi:</h4>
                      {previewVisualisasi.map(vis => {
                        const localVal = tampilkanTabelMap[vis.id_visualisasi] ?? !!vis.tampilkan_tabel;
                        const savedVal = !!vis.tampilkan_tabel;
                        const hasChanged = localVal !== savedVal;
                        return (
                          <div key={vis.id_visualisasi} className="chart-item" id={`chart-${vis.id_visualisasi}`}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <h4>{vis.judul}</h4>
                              {isEndUser() && vis.query_sql && vis.query_sql.includes(':') && (
                                <button className="view-button" onClick={() => handleEditParameter(vis)} style={{ fontSize: '0.9rem', padding: '5px 10px' }}>
                                  Edit Parameter
                                </button>
                              )}
                            </div>
                            {vis.deskripsi && (
                              <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '2px', marginBottom: '8px' }}>{vis.deskripsi}</p>
                            )}
                            {vis.jenis_grafik !== 'table' && (
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap' }}>
                                <span style={{
                                  fontSize: '0.8rem', padding: '3px 8px', borderRadius: '4px',
                                  backgroundColor: localVal ? '#e8f5e9' : '#fafafa',
                                  color: localVal ? '#2e7d32' : '#888',
                                  border: `1px solid ${localVal ? '#a5d6a7' : '#ddd'}`
                                }}>
                                  {localVal ? 'Tabel: Aktif ✓' : 'Tabel: Nonaktif'}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setTampilkanTabelMap(prev => ({ ...prev, [vis.id_visualisasi]: !localVal }))}
                                  style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid #ddd', cursor: 'pointer', backgroundColor: '#f5f5f5', fontSize: '0.8rem' }}
                                >
                                  {localVal ? 'Nonaktifkan' : 'Aktifkan'}
                                </button>
                                {hasChanged && (
                                  <button
                                    type="button"
                                    onClick={() => handleSaveTampilkanTabelVis(vis.id_visualisasi, localVal)}
                                    style={{ padding: '4px 10px', borderRadius: '4px', border: 'none', cursor: 'pointer', backgroundColor: '#4CAF50', color: 'white', fontSize: '0.8rem' }}
                                  >
                                    Simpan
                                  </button>
                                )}
                              </div>
                            )}
                            <div className={vis.jenis_grafik !== 'table' ? 'chart-container' : ''}>
                              {renderChart(vis, chartDataMap[vis.id_visualisasi])}
                            </div>
                            {localVal && vis.jenis_grafik !== 'table' && chartDataMap[vis.id_visualisasi] && (
                              <div style={{ marginTop: '10px' }}>
                                <TableWithPagination data={chartDataMap[vis.id_visualisasi]} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="analisis-section">
                      <h4>Interpretasi Hasil:</h4>
                      {isEditingAnalisis && (isEndUser() || isAnalis()) ? (
                        <textarea className="form-textarea" value={editedInterpretasi} onChange={(e) => setEditedInterpretasi(e.target.value)} rows={6} />
                      ) : (
                        <p>{selectedAnalisis.interpretasi_hasil}</p>
                      )}
                    </div>
                    
                    <div className="button-group">
                      {(isEndUser() || isAnalis()) && !isEditingAnalisis && (
                        <button className="save-button" onClick={handleStartEditAnalisis} style={{ marginRight: '10px' }}>
                          Edit Analisis
                        </button>
                      )}
                      {(isEndUser() || isAnalis()) && isEditingAnalisis && (
                        <>
                          <button className="save-button" onClick={handleSaveEditAnalisis} style={{ marginRight: '10px' }}>Simpan Perubahan</button>
                          <button className="cancel-button" onClick={handleCancelEditAnalisis} style={{ marginRight: '10px' }}>Batal</button>
                        </>
                      )}
                      <button className="download-button" onClick={downloadPDF}>Download PDF</button>
                    </div>
                    
                    {downloadSuccess && <div className="save-success-message">PDF berhasil diunduh!</div>}
                  </div>
                </div>
              )}
              
              {(isAdmin() || isAnalis()) && (
                <div className="button-container" style={{ marginTop: '20px', textAlign: 'center' }}>
                  <button className="create-button" style={{ padding: '10px 20px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '16px' }} onClick={handleCreateAnalisis}>
                    Buat Analisis
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      <EditParameterModal
        isOpen={showEditParameterModal}
        onClose={() => setShowEditParameterModal(false)}
        visualisasi={editingVisualisasi}
        onSave={handleSaveParameterEdit}
      />
    </Sidebar>
  );
};

export default KatalogAnalisis;
